/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import throttle from 'lodash.throttle';
import ResizeObserver from 'resize-observer-polyfill';
import { theme } from '../../../theme';
import { headerCellHeight } from '../constants';
import { TableGrid } from './TableGrid';
export function StickyHead({ children, intersectionTargetRef, containerRef, cellWidths, tableCss, }) {
    const stickyContainerRef = useRef(null);
    const [isVisible, setIsVisible] = useState(false);
    const [containerRect, setContainerRect] = useState({
        top: 0,
        left: 0,
        width: 0,
        height: 0,
    });
    const updateContainerDimensions = () => {
        var _a;
        const targetRect = (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
        const newRect = {
            left: (targetRect === null || targetRect === void 0 ? void 0 : targetRect.left) || 0,
            width: (targetRect === null || targetRect === void 0 ? void 0 : targetRect.width) || 0,
            top: 0,
            height: 48,
        };
        setContainerRect(newRect);
    };
    const updateContainerScrollPosition = () => {
        var _a;
        if (!containerRef.current)
            return;
        const xScrollOffset = containerRef.current.scrollLeft;
        (_a = stickyContainerRef.current) === null || _a === void 0 ? void 0 : _a.scrollTo(xScrollOffset, 0);
    };
    // Configure intersection observer to toggle sticky header on/off
    useEffect(() => {
        if (!intersectionTargetRef.current)
            return;
        const options = {
            threshold: 1.0,
        };
        const intersectionCallback = (entries) => {
            entries.forEach((entry) => {
                setIsVisible(!entry.isIntersecting);
            });
        };
        const intersectionObserver = new IntersectionObserver(intersectionCallback, options);
        intersectionObserver.observe(intersectionTargetRef.current);
        return () => intersectionObserver.disconnect(); // eslint-disable-line consistent-return
    }, [intersectionTargetRef.current]);
    // Calculate sticky header position and size to match table container
    useEffect(() => {
        if (!isVisible)
            return;
        if (!containerRef.current)
            return;
        updateContainerDimensions();
    }, [containerRef.current, isVisible]);
    // Update dimensions when container element is resized
    useEffect(() => {
        if (!isVisible)
            return;
        if (!containerRef.current)
            return;
        const resizeObserverCallback = throttle(updateContainerDimensions, 33); // Throttle events
        const resizeObserver = new ResizeObserver(resizeObserverCallback);
        resizeObserver.observe(containerRef.current);
        return () => resizeObserver.disconnect(); // eslint-disable-line consistent-return
    }, [containerRef.current, isVisible]);
    // Sync container scrolling
    useEffect(() => {
        updateContainerScrollPosition();
    }, [isVisible]);
    useEffect(() => {
        var _a;
        (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.addEventListener('scroll', updateContainerScrollPosition);
        return () => {
            var _a;
            (_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.removeEventListener('scroll', updateContainerScrollPosition);
        };
    }, [containerRef.current]);
    // Render sticky header
    if (!isVisible)
        return null;
    const containerStyle = Object.assign(Object.assign({}, containerRect), { height: headerCellHeight, position: 'fixed', zIndex: 5, overflowX: 'hidden', overflowY: 'hidden', borderBottom: `1px solid ${theme.colors.neutral.grey.base}` });
    // Generate styles to sync sticky columns with "real" columns
    const columnsCss = cellWidths
        .map((width, index) => `
    & th:nth-child(${index + 1}) {
      width: ${width}px;
    }
  `)
        .join('');
    // Append to generated table styles
    const allCss = tableCss + columnsCss;
    return (React.createElement("div", { style: containerStyle, ref: stickyContainerRef },
        React.createElement(TableGrid, { css: allCss },
            React.createElement("thead", null, children))));
}
//# sourceMappingURL=StickyHead.js.map